import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { displayPrice } from '../../../util/configHelpers';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
  STOCK_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../../util/types';
import { ensureOwnListing, filterWatermarkedImages } from '../../../util/data';
import {
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  createListingSlugWithId,
  createCategoryForUrl,
  createSubCategoryForUrl,
} from '../../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../../util/routes';
import { isBookingProcessAlias, isPurchaseProcessAlias } from '../../../transactions/transaction';
import {
  InlineTextButton,
  Modal,
  NamedLink,
  IconSpinner,
  PrimaryButtonInline,
} from '../../../components';
import { LISTING_STATUS } from '../../../constants/listing.constants';
import { USER_TYPE } from '../../../constants/user.constants';
import { PROMOTION_TYPE, SUBSCRIPTION_STATUS } from '../../../constants/payment.constants';
import { getPromotionPlanType } from '../../../components/PromotionModal/PromotionModal';
import useInterfaceLang from '../../../hooks/useInterfaceLang';
import { formatLocation } from '../../../util/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { handleToggleFavorites } from '../../ListingPage/ListingPage.shared';
import { updateProfile } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  isBusinessListingLimitReached,
  isBusinessSubscriptionInactive,
} from '../../../util/userHelpers';

import SubscriptionDetailsModal from '../ConfirmationModal/SubscriptionDetailsModal';
import StopListingModal from '../ConfirmationModal/StopListingModal';
import ListingActionsMenu from './ListingActionsMenu';
import ListingStatusRows from './ListingStatusRows';
import ListingCardMedia from './ListingCardMedia';
import ListingCardErrorStrip from './ListingCardErrorStrip';
import {
  deriveCardState,
  hasUnstoppedBilling,
  isCancelScheduled,
} from './listingCardState';
import { deriveMenuItems, derivePrimaryAction, hasAnyMenuItems } from './listingCardMenu';
import { deriveStatusRows } from './listingCardRows';
import { viewLinkProps } from './listingCardLinks';
import { DIALOG, useListingCardActions } from './useListingCardActions';
import { tabCompleted } from '../../EditListingPage/EditListingWizard/EditListingWizard';
import { DETAILS } from '../../EditListingPage/EditListingWizard/EditListingWizardTab';

import css from './ManageListingCard.module.css';

// Menu content needs the same padding
const MAX_LENGTH_FOR_WORDS_IN_TITLE = 7;

const priceData = (price, currency, intl) => {
  if (price?.currency === currency) {
    const amount = (price.amount / 100).toLocaleString('nl-NL'); // e.g. 32.500
    const formattedPrice = `€\u00A0${amount},-`;
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

function stripYearFromTitle(title) {
  return title.replace(/\b(17|18|19|20)\d{2}\b$/, '').trim();
}

// moved to listingCardLinks.js

// Cards are not fixed sizes - So, long words in title make flexboxed items to grow too big.
// 1. We split title to an array of words and spaces.
//    "foo bar".split(/([^\s]+)/gi) => ["", "foo", " ", "bar", ""]
// 2. Then we break long words by adding a '<span>' with word-break: 'break-all';
const formatTitle = (title, maxLength) => {
  const nonWhiteSpaceSequence = /([^\s]+)/gi;
  return title.split(nonWhiteSpaceSequence).map((word, index) => {
    return word.length > maxLength ? (
      <span key={index} style={{ wordBreak: 'break-all' }}>
        {word}
      </span>
    ) : (
      word
    );
  });
};

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(foundListingTypeConfig);
  if (showPrice && !price) {
    return (
      <div className={css.noPrice}>
        <FormattedMessage id="ManageListingCard.priceNotSet" />
      </div>
    );
  } else if (!showPrice) {
    return null;
  }
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ManageListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};


/**
 * Manage listing card
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.hasClosingError - Whether the closing error is present
 * @param {boolean} props.hasDiscardingError - Whether the discarding error is present
 * @param {boolean} props.hasOpeningError - Whether the opening error is present
 * @param {boolean} props.isMenuOpen - Whether the menu is open
 * @param {Object} [props.actionsInProgressListingId] - The actions in progress for the specific listing
 * @param {propTypes.uuid} [props.actionsInProgressListingId.uuid] - The uuid of the listing
 * @param {propTypes.ownListing} props.listing - The listing
 * @param {function} props.onCloseListing - The function to close the listing
 * @param {function} props.onOpenListing - The function to open the listing
 * @param {function} props.onDiscardDraft - The function to discard the draft
 * @param {string} [props.renderSizes] - The render sizes
 * @returns {JSX.Element} Manage listing card component
 */
export const ManageListingCard = props => {
  const { interfaceLang } = useInterfaceLang();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = props.intl || useIntl();
  const history = useHistory();
  const location = useLocation();

  const {
    className,
    rootClassName,
    relativeWrapperClassName,
    hasClosingError,
    hasDiscardingError,
    hasOpeningError,
    hasStatusError,
    hasSubscriptionCancelFailed,
    actionsInProgressListingId,
    listing,
    statistics,
    onCloseListing,
    onOpenListing,
    onDiscardDraft,
    onUpdateListingStatus,
    renderSizes,
    onPromoteListing,
    onCancelListingPromotion,
    onOpenPromotionModal,
    onManageDisableScrolling,
    enableBackgroundColorChange = false,
    disableOnClick = false,
    disableActions: disableActionsProp = false,
    hideStats = false,
    hideLabel = false,
    isOnSearchPage = false,
    isOnManageListingPage = false,
    isAboveFold = false,
  } = props;

  const currentListing = ensureOwnListing(listing);
  const id = currentListing.id.uuid;

  const { title = '', price, state, publicData, metadata } = listing.attributes;
  const { currentUser } = useSelector(state => state.user);
  const dispatch = useDispatch();

  const onUpdateFavorites = payload => dispatch(updateProfile(payload));

  const isFavorite = useMemo(
    () => currentUser?.attributes?.profile?.privateData?.favorites?.includes(listing?.id?.uuid),
    [currentUser, listing]
  );
  const { isPaused } = metadata || {};
  // Stat counts come from the BE (Mongo) via the `statistics` prop. Fall back to
  // Sharetribe metadata for safety if the stats fetch hasn't resolved yet.
  const {
    viewsCount = 0,
    sentInquiriesCount = 0,
    viewsWebsiteCount = 0,
    viewsPhoneCount = 0,
    searchImpressionsCount = 0,
    favoritesCount = 0,
  } = statistics || metadata || {};
  const slug = createSlug(title);
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;
  const isActive = state === LISTING_STATE_PUBLISHED;

  const { status } = publicData || {};
  const isSold = status === LISTING_STATUS.SOLD;
  const isCanceled = status === LISTING_STATUS.CANCELED;

  const displayTitle = stripYearFromTitle(title);

  const { userType } = currentUser?.attributes?.profile?.publicData || {};
  const isBusinessSeller = userType === USER_TYPE.BUSINESS_SELLER;

  const { subscription: userSubscription } = currentUser?.attributes?.profile?.metadata || {};
  const listingSubscription = !isBusinessSeller
    ? userSubscription?.listings?.find(l => l.listingId === id) || null
    : null;
  const isSubscriptionExpired = listingSubscription?.state === SUBSCRIPTION_STATUS.EXPIRED;

  // Has the details step been filled in? Only meaningful for the seller's own
  // advertisements, where the full listing is loaded — see hasRealTitle below.
  const isDetailsComplete = tabCompleted(DETAILS, currentListing, config, null, currentUser);

  // What this advertisement is doing, and therefore what can be done to it. All of
  // this is derived in pure modules so it can be tested — see listingCardState.test.js.
  const { state: cardState } = deriveCardState({
    listing: currentListing,
    listingSubscription,
    userType,
    isDetailsComplete,
  });
  const menuItems = deriveMenuItems({ state: cardState, isBusinessSeller });
  const primaryAction = derivePrimaryAction({ state: cardState });
  const statusRows = deriveStatusRows({ state: cardState, isBusinessSeller, listingSubscription });

  // Actions only exist where they are valid: this card is also used on search,
  // favourites, profile and the landing page, all of which pass `disableActions`.
  const showActions = !disableActionsProp && isOnManageListingPage;

  // Two reasons to warn that the billing did not stop: the action just failed, or the
  // data says so. The second survives a reload, which is what matters — the first
  // vanishes with the store while the direct debit carries on.
  const showBillingWarning =
    showActions &&
    (hasSubscriptionCancelFailed || hasUnstoppedBilling({ state: cardState, listingSubscription }));

  const actions = useListingCardActions({
    listing: currentListing,
    isDetailsComplete,
    onUpdateListingStatus,
    onOpenListing: (listingId, listingState) => handleOpenListing(listingId, listingState),
    onCloseListing,
    onDiscardDraft,
  });

  const primaryLinkProps = primaryAction ? actions.linkPropsByKey[primaryAction.key] : null;

  /**
   * The real title is built from manufacturer + model + year on the details step. Until
   * then the draft carries a stand-in (the category's own name), because Sharetribe
   * requires a title to create a draft at all — so the listing always *has* one, and
   * showing it would tell the seller their advertisement is called "Sloepen".
   *
   * Keyed on the details step itself, not on the card state: BLANK looked like the
   * right signal but stops applying the moment the seller pays, so a paid draft with
   * nothing filled in went back to displaying the stand-in.
   *
   * Scoped to the seller's own management view, because that is the only place a draft
   * is shown at all. Everywhere else this card appears — search, favourites, profile,
   * landing — the advertisements are published and their titles are real, and the
   * details check cannot even be answered there: those pages request a sparse fieldset,
   * so a required listing field they don't ask for (`publicData.model`, today) reads as
   * missing and turned every title on the page into the placeholder.
   */
  const hasRealTitle = !!displayTitle && (!isOnManageListingPage || isDetailsComplete);

  /**
   * The eyebrow above the title. `type` is only chosen on the details step, so a draft
   * that has just been created has a category and nothing else — and the old
   * `General.listing.type.<category>.<type>` key resolved to `…sloopsBoats.undefined`,
   * which react-intl renders as the raw key on the seller's card.
   *
   * Falls back to the category on its own, then to a placeholder.
   */
  const categoryLabelId = (() => {
    const { category, type } = publicData || {};
    if (category && type) {
      return `General.listing.type.${category}.${type}`;
    }
    if (category) {
      return `General.listing.category.${category}`;
    }
    return null;
  })();

  // `expiresAt` is the end of the paid period: the next charge while the subscription
  // renews, and the day the advertisement comes down once it is cancelled.
  const formatRowDate = value =>
    value ? intl.formatDate(new Date(value), { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const subscriptionDate = formatRowDate(
    statusRows.subscription?.dateField === 'updatedAt'
      ? listingSubscription?.updatedAt
      : listingSubscription?.expiresAt
  );
  const advertisementDate = formatRowDate(listingSubscription?.expiresAt);
  const planLabel = listingSubscription?.plan ? (
    <FormattedMessage id={`General.paymentMethods.${listingSubscription.plan}`} />
  ) : null;

  const classes = classNames(rootClassName || css.root, className, {
    [css.rootAutoPaused]: isPaused && !isSubscriptionExpired,
    [css.rootExpired]: isSubscriptionExpired,
  });

  const { listingType, transactionProcessAlias, yearBuilt, length, location: listingLocation } =
    publicData || {};

  const language = interfaceLang.split('-')[0];

  const address =
    typeof listingLocation?.address === 'object'
      ? formatLocation(listingLocation.address, language, true)
      : listingLocation?.address;

  const locationYearLengthInfo = [
    typeof length === 'number'
      ? `${length?.toFixed(2)}m`
      : typeof length === 'string'
      ? `${length}m`
      : null,
    yearBuilt,
    address,
  ]
    .filter(Boolean)
    .join(' | ');
  const isBookable = isBookingProcessAlias(transactionProcessAlias);
  const isProductOrder = isPurchaseProcessAlias(transactionProcessAlias);
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const { author } = listing;
  const isOwnListing = currentUser?.id?.uuid === author?.id?.uuid;
  const { companyName } = author?.attributes?.profile?.publicData || {};
  const { firstName, lastName, displayName: authorName } = author?.attributes?.profile || {};

  const displayName = companyName ?? authorName ?? `${firstName} ${lastName}`;

  const currentStock = currentListing.currentStock?.attributes?.quantity;
  const isOutOfStock = currentStock === 0;

  const optimizedImages = currentListing.attributes?.metadata?.optimizedImages;
  const { filteredImages, filteredOptimizedImages } = filterWatermarkedImages(
    currentListing.images,
    optimizedImages
  );
  const firstImage = filteredImages && filteredImages.length > 0 ? filteredImages[0] : null;

  // Subscription modal
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Listing limit modal (business sellers)
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const listingLimitReached = isBusinessListingLimitReached(currentUser);
  const subscriptionInactive = isBusinessSubscriptionInactive(currentUser);

  const handleOpenListing = (listingId, listingState) => {
    if (listingLimitReached) {
      setLimitModalOpen(true);
    } else {
      onOpenListing(listingId, listingState);
    }
  };

  // Video playback and viewport state now live in ListingCardMedia.
  const videoUrl = metadata?.videos?.mergedVideo?.url;

  const hasError = hasOpeningError || hasClosingError || hasDiscardingError || hasStatusError;
  const thisListingInProgress =
    actionsInProgressListingId && actionsInProgressListingId.uuid === id;

  const onOverListingLink = () => {
    // Enforce preloading of ListingPage (loadable component)
    const { component: Page } = findRouteByRouteName('ListingPage', routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };
  const commonParams = { params: { id: listing?.id?.uuid }, history, routes: routeConfiguration };

  const onToggleFavorites = handleToggleFavorites({
    ...commonParams,
    currentUser,
    onUpdateFavorites,
    location,
    setFavoriteSignal: () => {},
  });

  const titleClasses = classNames(css.title, {
    [css.titlePending]: isPendingApproval,
    [css.titleDraft]: isDraft,
  });

  const isLinkDisabled = isSubscriptionExpired;

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return (
    <div
      className={classNames(relativeWrapperClassName || css.relativePosition, {
        [css.relativePositionExpired]: isSubscriptionExpired,
      })}
    >
      {/* The ⋯ button lives here, outside the NamedLink that covers the card, so the
          menu's controls are not interactive elements nested inside an anchor. */}
      {showActions && hasAnyMenuItems({ state: cardState, isBusinessSeller }) && (
        <ListingActionsMenu
          items={menuItems}
          onSelect={actions.handleMenuSelect}
          linkPropsByKey={actions.linkPropsByKey}
          isOpen={actions.isMenuOpen}
          onToggleOpen={actions.setMenuOpen}
        />
      )}

      {onManageDisableScrolling && actions.activeDialog === DIALOG.SUBSCRIPTION && (
        <SubscriptionDetailsModal
          id={`subscriptionModal.${id}`}
          isOpen
          onClose={actions.closeDialog}
          onManageDisableScrolling={onManageDisableScrolling}
          listingId={id}
          subscription={listingSubscription}
          cardState={cardState}
          hasCancelFailed={showBillingWarning}
          hasCancelScheduled={isCancelScheduled(listingSubscription)}
        />
      )}

      {onManageDisableScrolling && actions.activeDialog === DIALOG.STOP && (
        <StopListingModal
          id={`stopListingModal.${id}`}
          isOpen
          onClose={actions.closeDialog}
          onManageDisableScrolling={onManageDisableScrolling}
          onConfirm={actions.handleStopConfirm}
          isBusinessSeller={isBusinessSeller}
          cardState={cardState}
          hasCancelScheduled={isCancelScheduled(listingSubscription)}
        />
      )}
      {onManageDisableScrolling && (
        <Modal
          id={`listingLimitModal.${id}`}
          isOpen={limitModalOpen}
          onClose={() => setLimitModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          usePortal
        >
          <div className={css.limitModalContent}>
            <p className={css.limitModalTitle}>
              <FormattedMessage
                id={
                  subscriptionInactive
                    ? 'ManageListingCard.subscriptionInactiveTitle'
                    : 'ManageListingCard.listingLimitReachedTitle'
                }
              />
            </p>
            <p>
              <FormattedMessage
                id={
                  subscriptionInactive
                    ? 'ManageListingCard.subscriptionInactiveMessage'
                    : 'ManageListingCard.listingLimitReachedMessage'
                }
              />
            </p>
          </div>
        </Modal>
      )}
      <NamedLink
        {...viewLinkProps(listing)}
        className={classes}
        aria-disabled={isLinkDisabled || undefined}
        onClick={isLinkDisabled ? e => e.preventDefault() : undefined}
        onMouseOver={!isLinkDisabled ? onOverListingLink : undefined}
        onTouchStart={!isLinkDisabled ? onOverListingLink : undefined}
      >
        <ListingCardMedia
          listing={currentListing}
          title={title}
          firstImage={firstImage}
          optimizedImages={filteredOptimizedImages}
          variants={variants}
          renderSizes={renderSizes}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          videoUrl={videoUrl}
          isAboveFold={isAboveFold}
          isFaded={isClosed}
          isDisabled={isSubscriptionExpired}
          isSold={isSold}
          isSubscriptionExpired={isSubscriptionExpired}
          isPendingApproval={isPendingApproval}
          isOnManageListingPage={isOnManageListingPage}
          hideLabel={hideLabel}
          showFavoriteButton={disableActionsProp && !isOwnListing}
          isFavorite={isFavorite}
          onToggleFavorites={onToggleFavorites}
          isInProgress={thisListingInProgress}
          hasError={hasError}
          errorMessage={intl.formatMessage({ id: 'ManageListingCard.actionFailed' })}
        />


        {/* Info area - NOT clickable for navigation */}
        <div className={css.info}>
          <div className={classNames(css.infoContent, { [css.faded]: isClosed })}>
            {/* Sits above everything else, because a seller who thinks they have
                stopped paying needs to see this before they see anything else. */}
            {showBillingWarning && (
              <ListingCardErrorStrip
                kind="subscriptionCancel"
                nextChargeDate={advertisementDate}
                onRetry={() => actions.openDialog(DIALOG.SUBSCRIPTION)}
              />
            )}
            <span className={classNames(css.category, { [css.categoryEmpty]: !categoryLabelId })}>
              <FormattedMessage id={categoryLabelId || 'ManageListingCard.categoryNotSet'} />
            </span>
            <div className={css.mainInfo}>
              <div
                className={classNames(css.titleWrapper, titleClasses, {
                  [css.titleEmpty]: !hasRealTitle,
                })}
              >
                {hasRealTitle ? (
                  formatTitle(displayTitle, MAX_LENGTH_FOR_WORDS_IN_TITLE)
                ) : (
                  <FormattedMessage id="ManageListingCard.titleNotSet" />
                )}
              </div>
              <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
              <span className={css.location}>
                {locationYearLengthInfo || (
                  <FormattedMessage id="ManageListingCard.locationNotSet" />
                )}
              </span>

              {isOnManageListingPage && (
                // <div className={css.statisticsWrapper}>
                <div className={classNames(css.statistics, { [css.statisticsPaused]: isPaused })}>
                  <span className={css.statistic}>
                    <span className={css.statisticData}>{viewsCount || '-'}</span>
                    <FormattedMessage id="ManageListingCard.listingViews" />
                  </span>
                  <span className={css.statistic}>
                    <span className={css.statisticData}>{sentInquiriesCount || '-'}</span>
                    <FormattedMessage id="ManageListingCard.sentInquires" />
                  </span>
                  <span className={css.statistic}>
                    <span className={css.statisticData}>{searchImpressionsCount || '-'}</span>
                    <FormattedMessage id="ManageListingCard.searchImpressions" />
                  </span>
                  <span className={css.statistic}>
                    <span className={css.statisticData}>{favoritesCount || '-'}</span>
                    <FormattedMessage id="ManageListingCard.favoritesCount" />
                  </span>
                </div>
                // </div>
              )}

              {showActions && (
                <ListingStatusRows
                  rows={statusRows}
                  advertisementDate={advertisementDate}
                  subscriptionDate={subscriptionDate}
                  planLabel={planLabel}
                />
              )}
            </div>
          </div>

          {/* One control outside the ⋯ menu, and only in the states where exactly one
              action matters — an unfinished or unpaid draft is the expensive failure,
              so getting the seller back into the flow earns a button. Everything else
              lives in the menu. */}
          {showActions && primaryAction && (
            <div
              className={css.cardActions}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {primaryLinkProps ? (
                <NamedLink
                  className={css.primaryAction}
                  name={primaryLinkProps.name}
                  params={primaryLinkProps.params}
                  onClick={e => e.stopPropagation()}
                >
                  <FormattedMessage id={primaryAction.labelId} />
                </NamedLink>
              ) : (
                <button
                  type="button"
                  className={css.primaryAction}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    actions.handleMenuSelect(primaryAction.key);
                  }}
                >
                  <FormattedMessage id={primaryAction.labelId} />
                </button>
              )}
            </div>
          )}
        </div>
      </NamedLink>
    </div>
  );
};

export default ManageListingCard;
